/**
 * @class
 */
import {completions as autocompletions} from '@netx/core-app/properties/autocomplete';
import Base from '@netx/core/lib/netx-collection';
import Model from '../models/CMSPage';

export default class CMSPages extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/**
	 * @property {object} methods - The methods available on this model
	 */
	get methods() {
		return {
			read: ['getPagesForPortal', this.portalId],
			fetchAll: ['getAllPages'],
			getPagesForKeyword: ['getPagesForKeyword', 'portalId', 'searchTerm'],
		};
	}

	/**
	 * parsers for rpc methods
	 * @type {object}
	 */
	get parsers() {
		return {
			/**
			 * @method
			 * @param {array} pages
			 */
			getPagesForPortal(pages) {
				this.set(pages, {parse: false});
				if (this.settingCurrent) {
					const currentId = this.settingCurrent;
					this.settingCurrent = false;
					this.setCurrentPage(currentId);
				}
			},
			/**
			 * @method
			 * currently unused
			 * @param {array} pages
			 */
			fetchAll(pages) {
				this.set(pages, {parse: false});
			},
		};
	}

	/** @override */
	constructor(models, opts = {}) {
		opts.model = opts.model || Model;

		super(models, opts);

		this.current = new this.model();

		/**
		 * id of a portal -- for getPagesForPortal()
		 * @type {string}
		 * @default
		 */
		this.portalId = opts.portalId;

		/**
		 * search term for getPagesForKeyword
		 * @type {string}
		 * @default
		 */
		this.searchTerm = opts.searchTerm;

		// Hey, listen!
		this.listenTo(dispatcher, 'cms:page:save:success', this.setFromCurrentPage)
			.on('sync add remove change', _.debounce(this.parseInPages, 50))
			.listenTo(
				dispatcher,
				'cms:action:delete:success',
				this.deleteActionSuccess,
			);
	}

	/**
	 * @method
	 * @param {int} portalId
	 * @param {object} opts
	 */
	fetchPortal(portalId, opts) {
		if (!parseInt(portalId)) return $.Deferred().reject();
		this.portalId = portalId;
		return this.fetch({duplicateQueryPolicy: 'abort', ...opts});
	}

	/**
	 * @method
	 * @param {object} action
	 */
	getPageForAction(action) {
		if (action.get('type') !== 'page') {
			return false;
		}

		const pageId = this.getPageIdFromAction(action.toJSON());

		return pageId ? this.get(pageId) : false;
	}
	/**
	 * @method
	 * @param {object} action
	 */
	getPageIdFromAction(actionOb) {
		const link = actionOb.link;
		const pageId = link ? Number(link.slice(6)) : null;

		return pageId || false;
	}
	/**
	 * @method
	 * @param {object} action
	 */
	deleteActionSuccess(action) {
		if (!action || action.type !== 'page') {
			// console.warn('bad action or not for page deletion');
			return;
		}

		const actionIdToDelete = this.getPageIdFromAction(action);

		if (!Number.isInteger(actionIdToDelete) || !this.portalId) {
			// console.warn('No page number for action');
			return;
		}

		// if pages have not been fetched yet, fetch
		if (this.length === 0) {
			this.fetchPortal(this.portalId).then((pages) => {
				this.destroyPageFromAction(actionIdToDelete);
			});
		} else {
			this.destroyPageFromAction(actionIdToDelete);
		}
	}
	/**
	 * @method
	 * @param {int} actionIdToDelete
	 */
	destroyPageFromAction(actionIdToDelete) {
		let page = this.get(actionIdToDelete);
		if (!page) return;

		this.remove(page);
		page.destroy({wait: true}).then(() => {
			dispatcher.trigger('cms:page:delete:success', page);
		});
	}

	/**
	 * @method
	 * @param {int} pageId
	 */
	setCurrentPage(pageId) {
		pageId = parseInt(pageId);

		const page = this.get(pageId);
		if (!page) {
			this.settingCurrent = pageId;
			return;
		}

		this.current.set(page.attributes);
	}

	/**
	 * @method
	 * @param {object} currentPageModel
	 */
	setFromCurrentPage(currentPageModel) {
		const page = this.get(currentPageModel.id);
		if (!page) return;

		page.set(currentPageModel.attributes);
	}

	/** @method */
	parseInPages() {
		// Victors HACK until we have an autolookup
		// That is cute - this code was copy pasted from a slack convo :p
		const pageAutocomplete = autocompletions.page;
		pageAutocomplete.data = this.map((page) => {
			return {label: page.get('slug'), value: page.slug};
		});
	}

	/**
	 * @method
	 * @param {string} searchStr
	 */
	searchPageContent(searchStr) {
		this.searchTerm = searchStr;
		return this.getPagesForKeyword();
	}

	/** @type {string} */
	static get __name__() {
		return 'CMSPages';
	}
}
